import React, { useState, useEffect, useCallback, useMemo } from "react"
import { Modal, Select } from "components/Core"
import { HiX, HiOutlineClipboardList, HiOutlineInbox, HiOutlineCube } from "react-icons/hi"
import useSearchMiddlewareOSRM from "hooks/useSearchMiddlewareOSRM"
import useOrchResources from "hooks/customs/useOrchResources"
import SgModalView from "screens/ItemsScreen/SgModalView"
import { format_datetime_by_local_client } from "helpers/datetime_helper"
import { Env, MultiClient } from "config"
import { CLIENT } from "config/init"
import cn from "classnames"

interface RelatedOrdersModalProps {
  isOpen: boolean
  onClose: () => void
  serie: any // { sku, serieSap, etc }
}

const RelatedOrdersModal = ({ isOpen, onClose, serie }: RelatedOrdersModalProps) => {
  const [_client] = MultiClient.get_user_env(null, Env.clients, Env.env, Env.defaults)
  const defaultChannelID = _client === CLIENT.ENTEL_PERU ? "TP" : ""

  const [currentChannel, setCurrentChannel] = useState<string>(defaultChannelID)
  const [channels, setChannels] = useState<any[]>([])
  const [selectedSg, setSelectedSg] = useState<any>(null)
  const [isChainLoading, setIsChainLoading] = useState(false)
  const [isFirstLoad, setIsFirstLoad] = useState(true)
  
  // Stock State
  const [stockInfo, setStockInfo] = useState<any[] | null>(null)
  const [loadingStock, setLoadingStock] = useState(false)
  
  // APIs
  const { data: data_channels, search_osrm_action: search_channels } = useSearchMiddlewareOSRM({ entity: "channels" })
  const { data: data_sg, search_osrm_action: search_sg, loading_app: loading_sg } = useSearchMiddlewareOSRM({ entity: "shipping_groups", silent404: true })
  const { data: data_reserves, search_osrm_action: search_reserves, loading_app: loading_reserves } = useSearchMiddlewareOSRM({ entity: "reserves", silent404: true })
  const { data: data_all_detailed_sg, search_osrm_action: search_all_detailed_sg, loading_app: loading_all_sg } = useSearchMiddlewareOSRM({ entity: "shipping_groups", silent404: true })
  const { post_source_items_inventory_by_channel_action } = useOrchResources()

  // 1. Fetch channels on mount
  useEffect(() => {
    if (isOpen) {
      search_channels({ from: 0, size: 1000, fields: ["id", "name"] })
    }
  }, [isOpen, search_channels])

  useEffect(() => {
    if (data_channels?.records) {
      const formatted = data_channels.records.map((chl: any) => ({
        id: chl.id,
        label: `${chl.name} (${chl.id})`,
        value: chl.id,
      }))
      setChannels([{ id: "", label: "Todos los canales", value: "" }, ...formatted])
    }
  }, [data_channels])

  // 2. Fetch Orders (shipping_groups)
  useEffect(() => {
    if (isOpen && serie?.serieSap) {
      const filters: any = {
        "logisticPlan.items.series.serie": serie.serieSap
      }
      if (currentChannel) {
        filters["channel"] = currentChannel
      }
      search_sg({
        from: 0,
        size: 50,
        filters
      })
    }
  }, [isOpen, serie, currentChannel, search_sg])

  // 3. Fetch Reserves based on salesChannelId from fetched primary orders
  useEffect(() => {
    if (data_sg?.records && data_sg.records.length > 0) {
      const salesChannelIds = Array.from(new Set(data_sg.records.map((sg: any) => sg.salesChannelId).filter(Boolean)))
      if (salesChannelIds.length > 0) {
        search_reserves({
          from: 0,
          size: 50,
          filters: {
            "keys": salesChannelIds
          }
        })
      }
    }
  }, [data_sg, search_reserves])

  // 4. Fetch ALL detailed orders based on ALL keys from reserves (to identify 404s)
  useEffect(() => {
    if (data_reserves?.records) {
      const allKeys = Array.from(new Set(data_reserves.records.flatMap((res: any) => res.keys || []).filter(Boolean)))
      if (allKeys.length > 0) {
        search_all_detailed_sg({
          from: 0,
          size: 100,
          filters: {
            "salesChannelId": allKeys
          }
        })
      }
    }
  }, [data_reserves, search_all_detailed_sg])

  // 4. Fetch Stock globally for this SKU and selected channel
  const fetchStock = useCallback(async () => {
    if (!currentChannel || !serie?.sku) return
    setLoadingStock(true)
    try {
      const resp: any = await post_source_items_inventory_by_channel_action({
        body: { filter: [serie.sku] },
        channel: currentChannel
      })
      setStockInfo(resp?.data?.message || [])
    } catch {
      setStockInfo([])
    } finally {
      setLoadingStock(false)
    }
  }, [currentChannel, serie, post_source_items_inventory_by_channel_action])

  useEffect(() => {
    if (isOpen) {
      fetchStock()
    } else {
      setStockInfo(null)
      setCurrentChannel(defaultChannelID)
    }
  }, [isOpen, currentChannel, fetchStock, defaultChannelID])

  // Unified Loading Chain Logic
  const anyLoading = loading_sg || loading_reserves || loading_all_sg || loadingStock
  
  useEffect(() => {
    if (isOpen) {
      setIsChainLoading(true)
    }
  }, [isOpen, currentChannel])

  useEffect(() => {
    if (isOpen) {
      // The chain is fully settled only when:
      // 1. None of the hooks are actively fetching.
      // 2. If we have SG records, we MUST have at least attempted a reserve fetch.
      // 3. If we have Reserve records, we MUST have at least attempted a detailed SG fetch.
      // This prevents the "gap" that causes flickering with multiple reserves.
      
      const hasPendingReserves = (data_sg?.records?.length ?? 0) > 0 && !data_reserves
      const hasPendingDetailed = (data_reserves?.records?.length ?? 0) > 0 && !data_all_detailed_sg
      
      const isActuallyBusy = anyLoading || hasPendingReserves || hasPendingDetailed

      if (!isActuallyBusy) {
        // Debounce to settle between sequential hook triggers (SG -> Reserve -> Detailed)
        const timer = setTimeout(() => {
            setIsChainLoading(false)
            setIsFirstLoad(false)
        }, 500) // Slightly increased for complex multi-reserve resolution
        return () => clearTimeout(timer)
      } else {
        setIsChainLoading(true)
      }
    } else {
      setIsFirstLoad(true)
    }
  }, [isOpen, anyLoading, data_sg, data_reserves, data_all_detailed_sg])

  const _reserves = useMemo(() => data_reserves?.records || [], [data_reserves])
  
  // Aggregate distinct keys from reserves
  const _all_related_keys = useMemo(() => {
    const sgKeys = data_sg?.records?.map((sg: any) => sg.salesChannelId) || []
    const reserveKeys = _reserves.flatMap((res: any) => res.keys || [])
    return Array.from(new Set([...sgKeys, ...reserveKeys])).filter(Boolean)
  }, [data_sg, _reserves])

  // Mapping for final orders display
  const _final_orders = useMemo(() => {
    return _all_related_keys.map(key => {
      const found = data_all_detailed_sg?.records?.find((sg: any) => sg.salesChannelId === key) 
                 || data_sg?.records?.find((sg: any) => sg.salesChannelId === key)
      return {
        key,
        data: found || null,
        exists: !!found
      }
    })
  }, [_all_related_keys, data_all_detailed_sg, data_sg])

  // Final unified list (Order-Centric)
  const _orders_with_context = useMemo(() => {
    return _final_orders.map(order => {
      const matchingReserve = _reserves.find((res: any) => res.keys?.includes(order.key))
      return {
        ...order,
        reserve: matchingReserve || null
      }
    })
  }, [_final_orders, _reserves])

  return (
    <>
      <Modal mounted={isOpen} className="!w-[800px] !max-w-[95vw] shadow-2xl border border-gray-100 overflow-hidden">
        <div className="w-full bg-[#F2F2F7] flex flex-col h-[85vh] max-h-[800px] relative">
          {/* Header */}
          <div className="p-5 px-6 border-b border-gray-200 bg-white/50 backdrop-blur-md relative z-20 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-6 sm:gap-4">
            <div className="flex items-center gap-5">
              <button
                onClick={onClose}
                className="shrink-0 w-11 h-11 bg-blue-600 hover:bg-blue-700 text-white rounded-full transition-all shadow-lg active:scale-90 flex items-center justify-center border border-blue-500/30"
              >
                <HiX className="text-2xl" />
              </button>
              <div className="flex flex-col">
              <div className="flex items-center gap-3 mb-1">
                <span className="text-[12px] font-bold text-gray-400 uppercase tracking-widest">
                  ID Serie
                </span>
                {serie?.sku && (
                  <span className="text-[10px] font-bold bg-gray-100 text-gray-500 px-2 py-0.5 rounded-md uppercase tracking-wider">
                    SKU: {serie.sku}
                  </span>
                )}
              </div>
              <h2 className="text-[24px] font-black text-gray-800 leading-none">
                <span className="text-blue-600">{serie?.serieSap}</span>
              </h2>
            </div>
          </div>
            
            {/* Filter */}
            <div className="flex flex-row items-center gap-2 bg-gray-100/80 p-1 rounded-full border border-gray-200 shadow-inner w-full sm:w-auto">
              <div className="pl-3.5 pr-1 flex items-center shrink-0">
                <span className="text-[11px] font-black text-gray-500 uppercase tracking-[0.1em]">Canal</span>
              </div>
              <div className="flex-1 sm:min-w-[220px]">
                <Select
                  options={channels}
                  value={currentChannel}
                  onChange={(v) => setCurrentChannel(v)}
                  label="Todos los canales"
                  withSearch
                  className="!min-h-[38px] !px-4 !rounded-full !bg-white !border-transparent shadow-sm hover:!bg-gray-50 !text-[13px] !font-bold !text-blue-600 transition-all !w-full"
                />
              </div>
            </div>
          </div>

          <div className="flex-1 min-h-0 overflow-y-auto p-6 flex flex-col gap-6">
            
            {/* Stock Box */}
            <div className="bg-white rounded-[16px] border border-gray-200 shadow-sm overflow-hidden flex flex-col shrink-0">
              <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between bg-gray-50/50">
                <div className="flex items-center gap-2">
                  <HiOutlineCube className="text-blue-500 text-lg" />
                  <h4 className="text-xs font-black text-gray-500 uppercase tracking-widest">Stock Disponible</h4>
                </div>
                {loadingStock && (
                  <span className="h-3.5 w-3.5 border-2 border-blue-200 border-t-blue-600 rounded-full animate-spin"></span>
                )}
              </div>
              <div className="px-5 py-5 min-h-[90px] flex items-center justify-center">
                {!currentChannel ? (
                  <p className="text-gray-400 font-medium italic text-center text-[13px]">
                    Seleccione un canal para consultar disponibilidad del SKU <span className="text-blue-600 font-bold">{serie?.sku}</span>
                  </p>
                ) : (loadingStock && !stockInfo) ? (
                   <div className="flex flex-col items-center gap-2">
                      <span className="h-5 w-5 border-2 border-blue-100 border-t-blue-500 rounded-full animate-spin"></span>
                   </div>
                ) : stockInfo && stockInfo.length > 0 ? (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 w-full">
                    {stockInfo.map((info: any, i: number) => (
                      <div key={i} className="flex flex-col p-3 bg-gray-50/50 rounded-xl border border-gray-100 items-center justify-center text-center">
                        <span 
                          className="text-[9px] font-black text-gray-400 uppercase tracking-widest mb-1 truncate w-full"
                          title={info.source || "General"}
                        >
                          {info.source || "General"}
                        </span>
                        <div className="flex items-baseline gap-1">
                          <span className="text-xl font-black text-gray-800">{info.qty_libre || 0}</span>
                          <span className="text-[10px] font-bold text-gray-400">UND</span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-400 text-center font-medium text-[13px]">
                    No se encontró stock en el canal <span className="font-bold">{currentChannel}</span>.
                  </p>
                )}
              </div>
            </div>

            <div className="flex flex-col gap-4 relative min-h-[300px]">
              {/* Initial Load Spinner (Centered) */}
              {(isChainLoading && isFirstLoad) && (
                <div className="py-20 text-center animate-in fade-in duration-300">
                  <span className="h-10 w-10 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin inline-block"></span>
                  <p className="mt-4 text-gray-400 text-[11px] font-black uppercase tracking-[0.2em] animate-pulse">Sincronizando registros...</p>
                </div>
              )}

              {/* Seamless Data View (with overlay if loading) */}
              {(!isFirstLoad || !isChainLoading) && (
                <div className={cn("flex flex-col gap-4 transition-all duration-500", isChainLoading ? "opacity-40 blur-[1px] pointer-events-none scale-[0.99]" : "opacity-100 blur-0")}>
                  {isChainLoading && (
                    <div className="absolute inset-x-0 -top-4 flex justify-center z-50">
                       <div className="bg-white/80 backdrop-blur-sm px-4 py-2 rounded-full border border-blue-100 shadow-lg flex items-center gap-2 animate-in slide-in-from-top-4 duration-300">
                         <span className="h-3 w-3 border-2 border-blue-200 border-t-blue-600 rounded-full animate-spin"></span>
                         <span className="text-[10px] font-black text-blue-600 uppercase tracking-widest">Actualizando...</span>
                       </div>
                    </div>
                  )}

                  {_orders_with_context.length === 0 ? (
                    <div className="py-20 text-center text-gray-400 italic font-medium bg-white/40 rounded-xl border border-dashed border-gray-300">
                      No se encontraton ordenes relacionados.
                    </div>
                  ) : (
                    _orders_with_context.map((order: any, i: number) => (
                      <div 
                        key={i}
                        onClick={() => order.exists && setSelectedSg(order.data)}
                        className={cn(
                          "bg-white rounded-[16px] border shadow-sm overflow-hidden flex flex-col transition-all group",
                          order.exists 
                            ? "border-gray-200 cursor-pointer hover:border-blue-300 hover:shadow-md" 
                            : "border-gray-100 bg-gray-50/50 cursor-default opacity-80"
                        )}
                      >
                        {/* Order Primary Header */}
                        <div className="px-5 py-4 flex flex-row justify-between items-center bg-white">
                          <div className="flex flex-col">
                            <div className="flex items-center gap-2 mb-1">
                              <HiOutlineClipboardList className={cn("text-[18px]", order.exists ? "text-blue-600" : "text-gray-400")} />
                              <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Orden Asociada</span>
                            </div>
                            <div className="flex items-center gap-2">
                               <h3 className={cn("text-[17px] font-black transition-colors", order.exists ? "text-gray-800 group-hover:text-blue-600" : "text-gray-400 font-bold")}>
                                {order.key || "Sin Nro."}
                              </h3>
                              {!order.exists && (
                                <span className="px-1.5 py-0.5 bg-red-50 text-red-500 text-[10px] font-bold rounded border border-red-100 uppercase tracking-tighter">
                                  No existe en Omnix
                                </span>
                              )}
                            </div>
                            {order.exists && (
                              <div className="flex items-center gap-2 mt-1">
                                <span className="text-[11px] font-semibold bg-gray-50 text-gray-500 px-2 py-0.5 rounded border border-gray-100">
                                  SG: {order.data.id}
                                </span>
                                <span className="text-[11px] text-gray-400 font-medium">
                                  {format_datetime_by_local_client(order.data.orderCreationDate, "dd MMM yyyy, HH:mm")}
                                </span>
                              </div>
                            )}
                          </div>
                          <div className="flex flex-col items-end">
                            <span className="text-[11px] font-bold text-gray-400 uppercase mb-0.5">Estado Omnix</span>
                            {order.exists ? (
                              <span className="text-[13px] font-bold text-blue-600 bg-blue-50 px-2 py-0.5 rounded-md">
                                {order.data?._omnixStateMachine?.currentState?.state?.name || "Desconocido"}
                              </span>
                            ) : (
                              <span className="text-[12px] font-bold text-gray-300 italic">404</span>
                            )}
                          </div>
                        </div>
    
                        {/* Reserve Secondary Context (Nested if exists) */}
                        {order.reserve && (
                          <div className="mx-4 mb-4 mt-1 p-3.5 bg-gray-50/50 rounded-[12px] border border-gray-100 flex items-center justify-between">
                            <div className="flex items-center gap-4">
                              <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center border border-gray-100 shadow-sm shrink-0">
                                 <HiOutlineInbox className="text-blue-500 text-lg" />
                              </div>
                              <div className="flex flex-col">
                                <div className="flex items-center gap-1.5">
                                  <span className="text-[9px] font-black text-gray-400 uppercase tracking-widest leading-none">Reserva</span>
                                  <div className="w-1 h-1 bg-gray-200 rounded-full"></div>
                                  <span className="text-[10px] font-bold text-blue-600 uppercase tracking-tight leading-none">{order.reserve.state || "Activa"}</span>
                                </div>
                                <span className="text-[13px] font-black text-gray-700 leading-tight mt-0.5">{order.reserve.id}</span>
                                <span className="text-[11px] text-gray-400 font-medium mt-0.5">
                                  Source: {order.reserve.sourceId || order.reserve.source || "-"}
                                </span>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>

          </div>
        </div>
      </Modal>

      {/* Extended Detail for Orders */}
      {selectedSg && (
        <SgModalView 
          sg={selectedSg} 
          onClose={() => setSelectedSg(null)} 
        />
      )}
    </>
  )
}

export default RelatedOrdersModal
